import { useLayoutEffect, useRef, useState } from 'react';

export interface TooltipContent {
  title: string;
  body: string;
  notes: string[];
}

interface Props {
  content: TooltipContent;
  x: number;
  y: number;
}

const OFFSET = 16;
const EDGE = 12;

/**
 * Structured content, not an HTML string. The old build stashed markup in a
 * data attribute and re-injected it with innerHTML; here nothing user-supplied
 * is ever parsed as HTML, so a subpopulation named `<img onerror=…>` is inert.
 */
export function Tooltip({ content, x, y }: Props) {
  const ref = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState({ left: x + OFFSET, top: y + OFFSET });

  useLayoutEffect(() => {
    const box = ref.current?.getBoundingClientRect();
    const width = box?.width ?? 0;
    const height = box?.height ?? 0;
    let left = x + OFFSET;
    let top = y + OFFSET;
    if (left + width > window.innerWidth - EDGE) left = x - width - OFFSET;
    if (top + height > window.innerHeight - EDGE) top = y - height - OFFSET;
    setPosition({ left: Math.max(EDGE, left), top: Math.max(EDGE, top) });
  }, [x, y, content]);

  return (
    <div ref={ref} className="flow-tooltip" role="tooltip" style={position}>
      <strong>{content.title}</strong>
      {content.body}
      {content.notes.map((note) => (
        <em key={note}>{note}</em>
      ))}
    </div>
  );
}
